import React from "react";
import {
  Box,
  Button,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import { tokens } from "../../theme";
import { useEffect } from "react";
import { useState } from "react";

const MoreFormModal = ({ isOpen, setModal, title }) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { success, snackError } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const [businessTypes, setBusinessTypes] = useState([]);

  // BVN Flow States
  const [bvnFlowStep, setBvnFlowStep] = useState("bvn_input"); // "bvn_input", "otp_input", "registration_form"
  const [bvnInput, setBvnInput] = useState("");
  const [dobInput, setDobInput] = useState(""); // YYYY-MM-DD
  const [otpVerificationToken, setOtpVerificationToken] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [verifiedBvnData, setVerifiedBvnData] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // Prefilled registration form fields
  const [regEmail, setRegEmail] = useState("");
  const [regBusinessName, setRegBusinessName] = useState("");
  const [regNotes, setRegNotes] = useState("");

  const handleAccountTypeChange = (e, setFieldValue) => {
    const val = e.target.value;
    setFieldValue("accountType", val);
    setBvnFlowStep("bvn_input");
    setBvnInput("");
    setDobInput("");
    setOtpInput("");
    setOtpVerificationToken("");
    setVerifiedBvnData(null);
    setRegEmail("");
    setRegBusinessName("");
    setRegNotes("");
  };

  const handleVerifyBvn = async () => {
    if (!bvnInput || bvnInput.length !== 11) {
      snackError("Please enter a valid 11-digit BVN");
      return;
    }
    if (!dobInput) {
      snackError("Please select/enter Date of Birth");
      return;
    }
    setIsVerifying(true);
    try {
      const response = await axiosPrivate.post("/auth-serviceV3/auth/bvn/verify", {
        bvn: bvnInput,
        dateOfBirth: dobInput,
        processType: "BASIC"
      });
      if (response?.data?.success) {
        setOtpVerificationToken(response?.data?.data?.otpVerificationToken);
        success(response?.data?.message || "OTP sent successfully!");
        setBvnFlowStep("otp_input");
      } else {
        const errMsg = response?.data?.error?.message || response?.data?.message || "BVN verification failed to initiate.";
        snackError(errMsg);
      }
    } catch (error) {
      console.log(error);
      const errMsg = error?.response?.data?.error?.message || error?.response?.data?.message || "Error verifying BVN.";
      snackError(errMsg);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpInput || otpInput.length < 4) {
      snackError("Please enter a valid OTP");
      return;
    }
    setIsVerifying(true);
    try {
      const response = await axiosPrivate.post("/auth-serviceV3/auth/bvn/verify-otp", {
        otpVerificationToken,
        otp: otpInput
      });
      if (response?.data?.success) {
        setVerifiedBvnData(response?.data?.data);
        success("BVN verified successfully!");
        setBvnFlowStep("registration_form");
      } else {
        const errMsg = response?.data?.error?.message || response?.data?.message || "OTP verification failed.";
        snackError(errMsg);
      }
    } catch (error) {
      console.log(error);
      const errMsg = error?.response?.data?.error?.message || error?.response?.data?.message || "Error verifying OTP.";
      snackError(errMsg);
    } finally {
      setIsVerifying(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "";
    let p = phone.trim();
    if (p.startsWith("0")) {
      return "+234" + p.slice(1);
    }
    if (!p.startsWith("+")) {
      return "+" + p;
    }
    return p;
  };

  const handleBvnRegisterSubmit = async (e, accountType) => {
    e.preventDefault();
    if (!regEmail || !regEmail.includes("@")) {
      snackError("Please enter a valid email address");
      return;
    }
    const isCorporate = accountType === "Corporate (Non-CAC)";
    if (isCorporate && !regBusinessName) {
      snackError("Please enter Business Name");
      return;
    }

    setIsVerifying(true);
    try {
      const endpoint = isCorporate
        ? "/auth-serviceV3/api/v1/admin/users/create-non-cac-account"
        : "/auth-serviceV3/api/v1/admin/users/create-personal-account";

      const payload = {
        email: regEmail,
        phoneNumber: formatPhone(verifiedBvnData.phoneNumber1),
        firstName: verifiedBvnData.firstName,
        lastName: verifiedBvnData.lastName,
        dateOfBirth: dobInput,
        bvn: verifiedBvnData.bvn || bvnInput,
        gender: verifiedBvnData.gender?.toUpperCase() || "MALE",
        address: {
          street: verifiedBvnData.residentialAddress || "",
          city: verifiedBvnData.lgaOfResidence || "",
          state: verifiedBvnData.stateOfOrigin || verifiedBvnData.stateOfResidence || "",
          country: verifiedBvnData.nationality || "Nigeria",
        },
        notes: regNotes,
      };

      if (isCorporate) {
        payload.businessName = regBusinessName;
      }

      const response = await axiosPrivate.post(endpoint, payload);
      if (response?.data?.success) {
        success(response?.data?.message || "Account created successfully!");
        handleClose();
      } else {
        const errMsg = response?.data?.error?.message || response?.data?.message || "Failed to create account.";
        snackError(errMsg);
      }
    } catch (error) {
      console.log(error);
      const errMsg = error?.response?.data?.error?.message || error?.response?.data?.message || "Error creating account.";
      snackError(errMsg);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFormSubmit = async (values) => {
    // No-op for the legacy submission as all new creation flows use handleBvnRegisterSubmit
    handleClose();
  };

  const initialValues = {
    accountType: "Individual",
  };

  const checkoutSchema = yup.object().shape({
    accountType: yup.string().required("Required"),
  });

  const options = [
    {
      name: "Individual",
    },
    {
      name: "Corporate (Non-CAC)",
    },
  ];

  const handleClose = () => {
    setModal((prev) => ({ ...prev, open: false }));
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          width: "50%",
          backgroundColor: colors.primary[400],
          p: 2,
          position: "relative",
          top: "50%",
          left: "55%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={checkoutSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            setFieldValue,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box padding={5}>
                <Typography
                  variant="h3"
                  sx={{ textAlign: "center", marginBottom: "30px" }}
                >
                  {title}
                </Typography>
                <Box
                  display="grid"
                  gap="20px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": {
                      gridColumn: isNonMobile ? undefined : "span 4",
                    },
                    marginBottom: "10px",
                  }}
                >
                  <TextField
                    label="Account Type"
                    fullWidth
                    select
                    name="accountType"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      handleAccountTypeChange(e, setFieldValue);
                    }}
                    value={values.accountType}
                    sx={{ gridColumn: "span 2" }}
                  >
                    {options.map((option, index) => (
                      <MenuItem key={index} value={option.name}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>



                  {(values.accountType === "Individual" || values.accountType === "Corporate (Non-CAC)") && (
                    <Box sx={{ gridColumn: "span 4", mt: 2 }}>
                      {bvnFlowStep === "bvn_input" && (
                        <Box display="flex" flexDirection="column" gap="20px">
                          <Typography variant="h5" color={colors.grey[100]} fontWeight="bold">
                            Step 1: Verify BVN Details
                          </Typography>
                          <TextField
                            fullWidth
                            variant="filled"
                            type="text"
                            label="BVN (11 Digits)"
                            value={bvnInput}
                            onChange={(e) => setBvnInput(e.target.value)}
                            inputProps={{ maxLength: 11 }}
                          />
                          <TextField
                            fullWidth
                            variant="filled"
                            type="date"
                            label="Date of Birth"
                            value={dobInput}
                            onChange={(e) => setDobInput(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          <Button
                            fullWidth
                            color="secondary"
                            variant="contained"
                            disabled={isVerifying || bvnInput.length !== 11 || !dobInput}
                            onClick={handleVerifyBvn}
                            sx={{ p: 1.5 }}
                          >
                            {isVerifying ? "Verifying..." : "Verify BVN"}
                          </Button>
                        </Box>
                      )}

                      {bvnFlowStep === "otp_input" && (
                        <Box display="flex" flexDirection="column" gap="20px">
                          <Typography variant="h5" color={colors.grey[100]} fontWeight="bold">
                            Step 2: Enter OTP Code
                          </Typography>
                          <Typography variant="body1" color={colors.grey[200]}>
                            An OTP has been sent to the phone number registered with this BVN. Please enter the OTP below.
                          </Typography>
                          <TextField
                            fullWidth
                            variant="filled"
                            type="text"
                            label="OTP Code"
                            value={otpInput}
                            onChange={(e) => setOtpInput(e.target.value)}
                          />
                          <Box display="flex" gap="10px">
                            <Button
                              fullWidth
                              color="warning"
                              variant="contained"
                              disabled={isVerifying}
                              onClick={() => setBvnFlowStep("bvn_input")}
                              sx={{ p: 1.5 }}
                            >
                              Back
                            </Button>
                            <Button
                              fullWidth
                              color="secondary"
                              variant="contained"
                              disabled={isVerifying || !otpInput}
                              onClick={handleVerifyOtp}
                              sx={{ p: 1.5 }}
                            >
                              {isVerifying ? "Verifying..." : "Verify OTP"}
                            </Button>
                          </Box>
                        </Box>
                      )}

                      {bvnFlowStep === "registration_form" && verifiedBvnData && (
                        <Box display="flex" flexDirection="column" gap="20px">
                          <Typography variant="h5" color={colors.grey[100]} fontWeight="bold">
                            Step 3: Complete Registration Details
                          </Typography>

                          {/* Prefilled / Readonly BVN Details */}
                          <Box
                            display="grid"
                            gridTemplateColumns="1fr 1fr"
                            gap="15px"
                            p={2}
                            sx={{
                              bgcolor: colors.primary[300] || "#1F2A40",
                              border: `1px solid ${colors.grey[700] || "#444"}`,
                              borderRadius: "5px"
                            }}
                          >
                            <Box>
                              <Typography variant="caption" color="#cccccc" sx={{ display: "block" }}>First Name</Typography>
                              <Typography variant="body1" fontWeight="bold" color="#ffffff">{verifiedBvnData.firstName || "N/A"}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="#cccccc" sx={{ display: "block" }}>Last Name</Typography>
                              <Typography variant="body1" fontWeight="bold" color="#ffffff">{verifiedBvnData.lastName || "N/A"}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="#cccccc" sx={{ display: "block" }}>Middle Name</Typography>
                              <Typography variant="body1" fontWeight="bold" color="#ffffff">{verifiedBvnData.middleName || "N/A"}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="#cccccc" sx={{ display: "block" }}>Date of Birth</Typography>
                              <Typography variant="body1" fontWeight="bold" color="#ffffff">{verifiedBvnData.dateOfBirth || "N/A"}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="#cccccc" sx={{ display: "block" }}>Gender</Typography>
                              <Typography variant="body1" fontWeight="bold" color="#ffffff">{verifiedBvnData.gender || "N/A"}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="#cccccc" sx={{ display: "block" }}>Phone Number</Typography>
                              <Typography variant="body1" fontWeight="bold" color="#ffffff">{verifiedBvnData.phoneNumber1 || "N/A"}</Typography>
                            </Box>
                            <Box sx={{ gridColumn: "span 2" }}>
                              <Typography variant="caption" color="#cccccc" sx={{ display: "block" }}>Residential Address</Typography>
                              <Typography variant="body1" fontWeight="bold" color="#ffffff">{verifiedBvnData.residentialAddress || "N/A"}</Typography>
                            </Box>
                          </Box>

                          {/* Inputs for Email, Business Name, Notes */}
                          <TextField
                            fullWidth
                            variant="filled"
                            type="email"
                            label="Email Address"
                            required
                            value={regEmail}
                            onChange={(e) => setRegEmail(e.target.value)}
                          />

                          {values.accountType === "Corporate (Non-CAC)" && (
                            <TextField
                              fullWidth
                              variant="filled"
                              type="text"
                              label="Business Name"
                              required
                              value={regBusinessName}
                              onChange={(e) => setRegBusinessName(e.target.value)}
                            />
                          )}

                          <TextField
                            fullWidth
                            variant="filled"
                            type="text"
                            label="Notes"
                            multiline
                            rows={2}
                            value={regNotes}
                            onChange={(e) => setRegNotes(e.target.value)}
                          />

                          <Box display="flex" gap="10px">
                            <Button
                              fullWidth
                              color="warning"
                              variant="contained"
                              disabled={isVerifying}
                              onClick={() => setBvnFlowStep("bvn_input")}
                              sx={{ p: 1.5 }}
                            >
                              Reset
                            </Button>
                            <Button
                              fullWidth
                              color="secondary"
                              variant="contained"
                              disabled={isVerifying || !regEmail}
                              onClick={(e) => handleBvnRegisterSubmit(e, values.accountType)}
                              sx={{ p: 1.5 }}
                            >
                              {isVerifying ? "Submitting..." : `Create ${values.accountType === "Corporate (Non-CAC)" ? "Corporate" : "Personal"} Account`}
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default MoreFormModal;
